import { useEffect } from "react";
import { resolveAssetPath } from "../../shared/utilities/assets";
import type { GalleryImage } from "../../types/wedding";

interface Props {
  images: GalleryImage[];
  index: number | null;
  copy: Record<string, string>;
  onChange: (index: number) => void;
  onClose: () => void;
}

export function GalleryViewer({ images, index, copy, onChange, onClose }: Props) {
  useEffect(() => {
    if (index === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onChange((index - 1 + images.length) % images.length);
      if (event.key === "ArrowRight") onChange((index + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, index, onChange, onClose]);

  if (index === null || !images[index]) return null;
  const image = images[index];
  const source = resolveAssetPath(image.url);
  return (
    <div className="lightbox is-visible" role="dialog" aria-modal="true" aria-label="Gallery photo preview">
      <button className="close-lightbox" type="button" onClick={onClose}>{copy.close}</button>
      <div className="lightbox-stage">
        <button className="lightbox-nav" type="button" aria-label={copy.previous} onClick={() => onChange((index - 1 + images.length) % images.length)}>‹</button>
        <figure className="lightbox-figure">
          <img src={source} alt={image.alt} />
          <figcaption>{index + 1} / {images.length}{image.caption ? ` - ${image.caption}` : ""}</figcaption>
        </figure>
        <button className="lightbox-nav" type="button" aria-label={copy.next} onClick={() => onChange((index + 1) % images.length)}>›</button>
      </div>
      <div className="lightbox-actions">
        <button className="lightbox-action" type="button" onClick={() => onChange((index - 1 + images.length) % images.length)}>{copy.previous}</button>
        <a className="lightbox-action lightbox-save" href={source} download>{copy.save}</a>
        <button className="lightbox-action" type="button" onClick={() => onChange((index + 1) % images.length)}>{copy.next}</button>
      </div>
    </div>
  );
}
